// Protocol-Process Mapping Service
// Complete CRUD operations for Protocol-Process Mappings
// Part of Policy Framework & EU Diseases Implementation

import { db } from '../firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  Timestamp 
} from 'firebase/firestore';

class ProtocolProcessMappingService {
  constructor() {
    this.collectionName = 'protocol_process_mapping';
    this.collectionRef = collection(db, this.collectionName);
  }

  /**
   * Create new mapping
   */
  async createMapping(data, userId, userName) {
    try {
      // Validation
      if (!data.protocolId || !data.processId) {
        throw new Error('Protocol ID and Process ID are required');
      }

      // Check if mapping already exists
      const existing = await this.getMappingByProtocolAndProcess(
        data.protocolId,
        data.processId
      );

      if (existing) {
        throw new Error('Mapping already exists');
      }

      const now = Timestamp.now();

      const mappingData = {
        ...data,
        createdAt: now,
        createdBy: userId,
        createdByName: userName || userId,
        updatedAt: now,
        updatedBy: userId,
        updatedByName: userName || userId
      };

      // Remove undefined values
      const cleanedData = this.removeUndefinedValues(mappingData);

      const docRef = await addDoc(this.collectionRef, cleanedData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating mapping:', error);
      throw error;
    }
  }

  /**
   * Get all mappings for an organization
   */
  async getMappings(organizationId, filters = {}) {
    try {
      let q = query(
        this.collectionRef,
        where('organizationId', '==', organizationId)
      );

      if (filters.protocolId) {
        q = query(q, where('protocolId', '==', filters.protocolId));
      }

      if (filters.processId) {
        q = query(q, where('processId', '==', filters.processId));
      }

      if (filters.policyFrameworkId) {
        q = query(q, where('policyFrameworkId', '==', filters.policyFrameworkId));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting mappings:', error);
      throw error;
    }
  }

  /**
   * Get mapping by protocol and process
   */
  async getMappingByProtocolAndProcess(protocolId, processId) {
    try {
      const q = query(
        this.collectionRef,
        where('protocolId', '==', protocolId),
        where('processId', '==', processId)
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }

      return {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data()
      };
    } catch (error) {
      console.error('Error getting mapping:', error);
      throw error;
    }
  }

  /**
   * Get processes for a protocol
   */
  async getProcessesForProtocol(protocolId) {
    try {
      const q = query(
        this.collectionRef,
        where('protocolId', '==', protocolId)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting processes for protocol:', error);
      throw error;
    }
  }

  /**
   * Get protocols for a process
   */
  async getProtocolsForProcess(processId) {
    try {
      const q = query(
        this.collectionRef,
        where('processId', '==', processId)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting protocols for process:', error);
      throw error;
    }
  }

  /**
   * Update mapping
   */
  async updateMapping(mappingId, updates, userId, userName) {
    try {
      const docRef = doc(this.collectionRef, mappingId);
      
      const updateData = {
        ...updates,
        updatedAt: Timestamp.now(),
        updatedBy: userId,
        updatedByName: userName || userId
      };

      // Remove undefined values
      const cleanedData = this.removeUndefinedValues(updateData);

      await updateDoc(docRef, cleanedData);
      return mappingId;
    } catch (error) {
      console.error('Error updating mapping:', error);
      throw error;
    }
  }

  /**
   * Delete mapping
   */
  async deleteMapping(mappingId) {
    try {
      const docRef = doc(this.collectionRef, mappingId);
      await deleteDoc(docRef);
      return mappingId;
    } catch (error) {
      console.error('Error deleting mapping:', error);
      throw error;
    }
  }

  /**
   * Bulk create mappings
   */
  async bulkCreateMappings(mappingsData, userId, userName) {
    try {
      const results = {
        created: [],
        failed: []
      };

      for (const data of mappingsData) {
        try {
          const id = await this.createMapping(data, userId, userName);
          results.created.push({ id, ...data });
        } catch (error) {
          results.failed.push({ data, error: error.message });
        }
      }

      return results;
    } catch (error) {
      console.error('Error bulk creating mappings:', error);
      throw error;
    }
  }

  /**
   * Helper: Remove undefined values from object
   */
  removeUndefinedValues(obj) {
    if (obj === null || obj === undefined) {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.removeUndefinedValues(item));
    }
    
    if (typeof obj !== 'object') {
      return obj;
    }
    
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = this.removeUndefinedValues(value);
      }
    }
    return cleaned;
  }
}

export default new ProtocolProcessMappingService();

